import { cn } from '@/lib/utils';
import { Rating, FeedbackRating } from '@/utils/feedback';
import React from 'react';
import { Button } from '@components/components/ui/button';

interface RatingButtonProps {
  rating: Rating;
  currentRating: FeedbackRating | null;
  onRatingClick: (value: FeedbackRating) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

const RatingButton: React.FC<RatingButtonProps> = ({
  rating,
  currentRating,
  onRatingClick,
  disabled = false,
  size = 'md',
}) => {
  const IconComponent = rating.icon;
  const iconSize = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';
  const buttonSize = size === 'sm' ? 'sm' : 'icon';
  const isSelected = currentRating === rating.value;
  const isPositive = rating.value === FeedbackRating.POSITIVE;

  return (
    <Button
      onClick={() => onRatingClick(rating.value)}
      variant="outline"
      size={buttonSize}
      disabled={disabled}
      title={rating.label}
      aria-label={rating.label}
      className={cn(
        // Base styles (unselected)
        !isSelected && [
          'bg-white border-gray-300 text-gray-600',
          'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400',
        ],
        // Selected styles - Positive (ThumbsUp) - Blue background with white icon
        isSelected &&
          isPositive && [
            'bg-[#0472E1] border-[#0472E1] text-white',
            'hover:bg-[#0366CC] hover:border-[#0366CC] hover:text-white',
            'focus-visible:ring-[#0472E1]',
          ],
        // Selected styles - Negative (ThumbsDown) - Red background with white icon
        isSelected &&
          !isPositive && [
            'bg-[#DE1827] border-[#DE1827] text-white',
            'hover:bg-[#C71522] hover:border-[#C71522] hover:text-white',
            'focus-visible:ring-[#DE1827]',
          ]
      )}
    >
      <IconComponent
        strokeWidth={2}
        className={cn('transition-colors', iconSize, '[&_svg]:size-auto')}
      />
    </Button>
  );
};

export default RatingButton;
